import { Loader2 } from 'lucide-react';
import { Checkbox } from '../../components/ui/Checkbox';
import { useBrevoLists } from '../../queries/brevoLists';
import { useAssociateSiteList, useDisassociateSiteList, useSiteBrevoLists } from '../../queries/brevoSiteLists';

// The old admin renders a site x list matrix (one column per known site_key) -
// but each admin-api deployment is already brand-scoped to its own database
// and its own Brevo account, so there's structurally only ever one site_key
// here. Simplified to a single-column "associated with this site" toggle.
export function SiteListsPage() {
  const { data: lists = [], isLoading: listsLoading } = useBrevoLists({ all: true });
  const { data: siteLists, isLoading: siteListsLoading } = useSiteBrevoLists();
  const associate = useAssociateSiteList();
  const disassociate = useDisassociateSiteList();

  const associatedIds = new Set((siteLists?.associations ?? []).map((a) => a.brevo_list_id));

  const toggle = (listId: number, listName: string) => {
    if (associatedIds.has(listId)) {
      disassociate.mutate(listId);
    } else {
      associate.mutate({ brevoListId: listId, listName });
    }
  };

  const isLoading = listsLoading || siteListsLoading;

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">Lists associated with {siteLists?.siteKey ?? 'this site'}.</p>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2">List</th>
                <th className="px-3 py-2">Subscribers</th>
                <th className="px-3 py-2">Associated</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {lists.map((list) => (
                <tr key={list.id}>
                  <td className="px-3 py-2 font-medium">{list.name}</td>
                  <td className="px-3 py-2 text-muted-foreground">{list.subscriberCount.toLocaleString()}</td>
                  <td className="px-3 py-2">
                    <Checkbox
                      checked={associatedIds.has(list.id)}
                      onChange={() => toggle(list.id, list.name)}
                      disabled={associate.isPending || disassociate.isPending}
                    />
                  </td>
                </tr>
              ))}
              {lists.length === 0 && (
                <tr>
                  <td colSpan={3} className="px-3 py-8 text-center text-muted-foreground">
                    No Brevo lists found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
